const iconModules = import.meta.glob(
	"../../../../assets/img/icons/**/*.{png,gif,jpg,svg}",
	{ eager: true, import: "default" },
) as Record<string, string>;

const uiModules = import.meta.glob(
	"../../../../assets/img/ui/**/*.{png,gif,jpg,svg}",
	{ eager: true, import: "default" },
) as Record<string, string>;

const icon = (filename: string): string => {
	const key = `../../../../assets/img/icons/${filename}`;
	return iconModules[key] ?? "";
};

const ui = (filename: string): string => {
	const key = `../../../../assets/img/ui/${filename}`;
	return uiModules[key] ?? "";
};

// Recursive type for consumer icon namespaces.
// Values are URL strings or nested objects of URL strings (up to 2 levels).
export type ClassicyIconMap = Record<
	string,
	string | Record<string, string | Record<string, string>>
>;

/**
 * Register custom icon namespaces into ClassicyIcons at runtime.
 *
 * Call this once before rendering — typically in your app entry point.
 * The function returns its argument with full TypeScript types so you can
 * keep a typed local reference for autocomplete:
 *
 * ```ts
 * import appIcon from './icon.png';
 * const MyIcons = registerClassicyIcons({ myApp: { icon: appIcon } });
 * // MyIcons.myApp.icon  — fully typed
 * // ClassicyIcons['myApp']['icon']  — also works at runtime
 * ```
 */
export function registerClassicyIcons<
	T extends Record<string, ClassicyIconMap>,
>(icons: T): T {
	Object.assign(ClassicyIcons as Record<string, unknown>, icons);
	return icons;
}

export const ClassicyIcons = {
	classicy: {
		logo: ui("classicy-logo.svg"),
		bg: ui("earth.png"),
	},
	applications: {
		// The EPG icon set (station logos, ratings badges, cc/language marks)
		// moved out of classicy: it is product-lineup-specific, so the
		// consuming app registers it at runtime via registerClassicyIcons
		// under applications.epg.
		internetExplorer: {
			app: icon("applications/internet-explorer/app.png"),
			apple: icon("applications/internet-explorer/apple.png"),
			documentWarn: icon("applications/internet-explorer/document-warn.png"),
			document: icon("applications/internet-explorer/document.png"),
			downloadingFileAnimated: icon(
				"applications/internet-explorer/downloading-file-animated.png",
			),
			downloadingFile: icon(
				"applications/internet-explorer/downloading-file.png",
			),
			forward: icon("applications/internet-explorer/forward.png"),
			forwardOn: icon("applications/internet-explorer/forward-on.png"),
			backward: icon("applications/internet-explorer/backward.png"),
			backwardOn: icon("applications/internet-explorer/backward-on.png"),
			loaderAnimated: icon(
				"applications/internet-explorer/loader-animated.png",
			),
			loader: icon("applications/internet-explorer/loader.png"),
			refresh: icon("applications/internet-explorer/refresh.png"),
			refreshOn: icon("applications/internet-explorer/refresh-on.png"),
			clipboard: icon("applications/internet-explorer/clipboard.png"),
			compass: icon("applications/internet-explorer/compass.png"),
			documentDownload: icon(
				"applications/internet-explorer/document-download.png",
			),
			documentFolder: icon(
				"applications/internet-explorer/document-folder.png",
			),
			documentHome: icon("applications/internet-explorer/document-home.png"),
			documentLink: icon("applications/internet-explorer/document-link.png"),
			documentMoveToFolder: icon(
				"applications/internet-explorer/document-move-to-folder.png",
			),
			documentNews: icon("applications/internet-explorer/document-news.png"),
			documentShortcut: icon(
				"applications/internet-explorer/document-shortcut.png",
			),
			documentShortcutEdit: icon(
				"applications/internet-explorer/document-shortcut-edit.png",
			),
			documentSound: icon("applications/internet-explorer/document-sound.png"),
			documentWarning: icon(
				"applications/internet-explorer/document-warning.png",
			),
			editor: icon("applications/internet-explorer/editor.png"),
			favorites: icon("applications/internet-explorer/compass.png"),
			favoritesOn: icon("applications/internet-explorer/compass.png"),
			find: icon("applications/internet-explorer/find.png"),
			globe: icon("applications/internet-explorer/globe.png"),
			logo: icon("applications/internet-explorer/logo.png"),
			mailbox: icon("applications/internet-explorer/mailbox.png"),
			news: icon("applications/internet-explorer/news.png"),
			preferences: icon("applications/internet-explorer/preferences.png"),
			print: icon("applications/internet-explorer/print.png"),
			search: icon("applications/internet-explorer/search.png"),
		},
		netscape: {
			loaderAnimated: icon("applications/netscape/loader-animated.png"),
			loader: icon("applications/netscape/loader.png"),
		},
		pager: {
			app: icon("applications/pager/app.png"),
		},
		news: {
			app: icon("applications/news/app.png"),
		},
		pictureViewer: {
			app: icon("applications/picture-viewer/app.png"),
		},
		radio: {
			app: icon("applications/radio/app.png"),
		},
		registerWithApple: {
			app: icon("applications/register-with-apple/app.png"),
		},
		setupAssistant: {
			setupAssistant: icon("applications/setup-assistant/setup-assistant.png"),
		},
		stickies: {
			app: icon("applications/stickies/app.png"),
			controlButtonSprites: icon(
				"applications/stickies/control_button_sprites.png",
			),
			notes: icon("applications/stickies/notes.png"),
			stack: icon("applications/stickies/stack.png"),
		},
		stuffit: {
			app: icon("applications/stuffit/app.png"),
			compressing: icon("applications/stuffit/compressing.png"),
			file: icon("applications/stuffit/file.png"),
		},
		usenet: {
			app: icon("applications/usenet/usenet.png"),
			search: icon("applications/usenet/usenet_search.png"),
			server: icon("applications/usenet/usenet_server.png"),
		},
		systemProfiler: {
			app: icon("applications/system-profiler/app.png"),
		},
		simpletext: {
			app: icon("applications/simpletext/app.png"),
		},
	},
	controlPanels: {
		appearanceManager: {
			app: icon("control-panels/appearance-manager/app.png"),
			backgroundCenter: icon(
				"control-panels/appearance-manager/background-center.png",
			),
			backgroundFill: icon(
				"control-panels/appearance-manager/background-fill.png",
			),
			backgroundScale: icon(
				"control-panels/appearance-manager/background-scale.png",
			),
			backgroundTile: icon(
				"control-panels/appearance-manager/background-tile.png",
			),
			file: icon("control-panels/appearance-manager/file.png"),
			platinum: icon("control-panels/appearance-manager/platinum.png"),
			preferences: icon("control-panels/appearance-manager/preferences.png"),
		},
		dateTimeManager: {
			dateTimeManager: icon(
				"control-panels/date-time-manager/date-time-manager.png",
			),
		},
		keyboard: {
			app: icon("control-panels/keyboard/app.png"),
		},
		location: {
			app: icon("control-panels/location/app.png"),
			map: icon("control-panels/location/map.png"),
		},
		soundManager: {
			app: icon("control-panels/sound-manager/app.png"),
			extension: icon("control-panels/sound-manager/extension.png"),
			preferences: icon("control-panels/sound-manager/preferences.png"),
			sound33: icon("control-panels/sound-manager/sound-33.png"),
			sound66: icon("control-panels/sound-manager/sound-66.png"),
			soundMute: icon("control-panels/sound-manager/sound-mute.png"),
			soundOff: icon("control-panels/sound-manager/sound-off.png"),
			soundOn: icon("control-panels/sound-manager/sound-on.png"),
			stt: icon("control-panels/sound-manager/stt.png"),
			tts: icon("control-panels/sound-manager/tts.png"),
		},
	},
	system: {
		alias: icon("system/alias.png"),
		appleBlackOnWhite: icon("system/apple-black-on-white.png"),
		appleWhiteOnBlack: icon("system/apple-white-on-black.png"),
		apple: icon("system/apple.png"),
		bigSwitchOff: icon("system/big-switch-off.png"),
		bigSwitchOn: icon("system/big-switch-on.png"),
		bomb: icon("system/bomb.svg"),
		clipboard: icon("system/clipboard.png"),
		comport1: icon("system/comport1.png"),
		comport2: icon("system/comport2.png"),
		error: icon("system/error.png"),
		extension: icon("system/extension.png"),
		help: icon("system/help.png"),
		info: icon("system/info.png"),
		internet: icon("system/internet.png"),
		macClassic: icon("system/mac-classic.png"),
		macWink: icon("system/mac-wink.png"),
		mac: icon("system/mac.png"),
		macos: icon("system/macos.png"),
		macosSvg: icon("system/macos.svg"),
		pig: icon("system/pig.png"),
		printer: icon("system/printer.png"),
		question: icon("system/question.png"),
		shockwave: icon("system/shockwave.png"),
		systemResources: icon("system/system-resources.png"),
		warn: icon("system/warn.png"),
		windows: icon("system/windows.png"),
		colors: {
			extension: icon("system/colors/extension.png"),
			pickerCmyk: icon("system/colors/picker-cmyk.png"),
			pickerCrayon: icon("system/colors/picker-crayon.png"),
			pickerHex: icon("system/colors/picker-hex.png"),
			pickerHls: icon("system/colors/picker-hls.png"),
			pickerHsv: icon("system/colors/picker-hsv.png"),
			pickerRgb: icon("system/colors/picker-rgb.png"),
		},
		desktop: {
			desktop: icon("system/desktop/desktop.png"),
			trashEmpty: icon("system/desktop/trash-empty.png"),
			trashFull: icon("system/desktop/trash-full.png"),
		},
		display: {
			brightness: icon("system/display/brightness.png"),
			contrast: icon("system/display/contrast.png"),
		},
		drives: {
			cdrom: icon("system/drives/cdrom.png"),
			disk: icon("system/drives/disk.png"),
			driveExternal: icon("system/drives/drive-external.png"),
			dvdAudio: icon("system/drives/dvd-audio.png"),
			dvdVideo: icon("system/drives/dvd-video.png"),
			dvd: icon("system/drives/dvd.png"),
			floppyMac: icon("system/drives/floppy-mac.png"),
			floppyPc: icon("system/drives/floppy-pc.png"),
			floppy: icon("system/drives/floppy.png"),
			internetDrive: icon("system/drives/internet-drive.png"),
			networkDrive: icon("system/drives/network-drive.png"),
			raid: icon("system/drives/raid.png"),
			rom: icon("system/drives/rom.png"),
		},
		extensions: {
			appleGuide: icon("system/extensions/apple-guide.png"),
			appleShare: icon("system/extensions/apple-share.png"),
			appleVision: icon("system/extensions/apple-vision.png"),
			contextMenus: icon("system/extensions/context-menus.png"),
			dateAndTime: icon("system/extensions/date-and-time.png"),
			ethernet: icon("system/extensions/ethernet.png"),
			fmRadio: icon("system/extensions/fm-radio.png"),
			quickdraw: icon("system/extensions/quickdraw.png"),
			quicktime: icon("system/extensions/quicktime.png"),
			quicktimeMpeg: icon("system/extensions/quicktime-mpeg.png"),
			screensaver: icon("system/extensions/screensaver.png"),
			soundManager: icon("system/extensions/sound-manager.png"),
		},
		files: {
			_1292: icon("system/files/129 2.png"),
			_1294: icon("system/files/129 4.png"),
			_229: icon("system/files/229.png"),
			_232Messages: icon("system/files/232.Messages.png"),
			application: icon("system/files/application.png"),
			document: icon("system/files/document.png"),
			edition: icon("system/files/edition.png"),
			fileInvisible: icon("system/files/file-invisible.png"),
			filePc: icon("system/files/file-pc.png"),
			fileText: icon("system/files/file-text.png"),
			file: icon("system/files/file.png"),
			internationalization: icon("system/files/internationalization.png"),
			note: icon("system/files/note.png"),
			photo: icon("system/files/photo.png"),
			preferences: icon("system/files/preferences.png"),
			script: icon("system/files/script.png"),
			settingsSync: icon("system/files/settings-sync.png"),
			slideshow: icon("system/files/slideshow.png"),
			sound: icon("system/files/sound.png"),
			stationaryBlack: icon("system/files/stationary-black.png"),
		},
		folders: {
			appleMenuItem: icon("system/folders/apple_menu_item.png"),
			applicationSupport: icon("system/folders/application_support.png"),
			applications: icon("system/folders/applications.png"),
			assistant: icon("system/folders/assistant.png"),
			colorsyncProfiles: icon("system/folders/colorsync_profiles.png"),
			compressed: icon("system/folders/compressed.png"),
			contextualMenuItems: icon("system/folders/contextual_menu_items.png"),
			controlPanels: icon("system/folders/control_panels.png"),
			controlStrip: icon("system/folders/control_strip.png"),
			directory: icon("system/folders/directory.png"),
			extensions: icon("system/folders/extensions.png"),
			extras: icon("system/folders/extras.png"),
			favorites: icon("system/folders/favorites.png"),
			folderApple: icon("system/folders/folder-apple.png"),
			folderExtensions: icon("system/folders/folder-extensions.png"),
			folderFlip: icon("system/folders/folder-flip.png"),
			folderFonts: icon("system/folders/folder-fonts.png"),
			folderLdap: icon("system/folders/folder-ldap.png"),
			folderPreferences: icon("system/folders/folder-preferences.png"),
			folderPrinters: icon("system/folders/folder-printers.png"),
			folderSystemProtected: icon("system/folders/folder-system-protected.png"),
			folderSystem: icon("system/folders/folder-system.png"),
			fonts: icon("system/folders/fonts.png"),
			help: icon("system/folders/help.png"),
			internet: icon("system/folders/internet.png"),
			internetSearchSites: icon("system/folders/internet_search_sites.png"),
			languageAndRegion: icon("system/folders/language_and_region.png"),
			preferences: icon("system/folders/preferences.png"),
			recentDocuments: icon("system/folders/recent_documents.png"),
			scripts: icon("system/folders/scripts.png"),
			startupItems: icon("system/folders/startup_items.png"),
			suitcaseSystem: icon("system/folders/suitcase-system.png"),
			suitcase: icon("system/folders/suitcase.png"),
			suitecaseSystem: icon("system/folders/suitecase-system.png"),
			system: icon("system/folders/system.png"),
			textEncodings: icon("system/folders/text_encodings.png"),
			utilities: icon("system/folders/utilities.png"),
		},
		network: {
			chooser: icon("system/network/chooser.png"),
			controlpanelInternet: icon("system/network/controlpanel-internet.png"),
			crypto: icon("system/network/crypto.png"),
			fileServer: icon("system/network/file-server.png"),
			globeAnimated: icon("system/network/globe-animated.png"),
			globe: icon("system/network/globe.png"),
			internetConfig: icon("system/network/internet-config.png"),
			internetServices: icon("system/network/internet-services.png"),
			internet: icon("system/network/internet.png"),
			ldapDirectory: icon("system/network/ldap-directory.png"),
			modem: icon("system/network/modem.png"),
			networkLocal: icon("system/network/network-local.png"),
			networkPublic: icon("system/network/network-public.png"),
			sslInvalid: icon("system/network/ssl-invalid.png"),
			sslWeb: icon("system/network/ssl-web.png"),
			ssl: icon("system/network/ssl.png"),
			terminal: icon("system/network/terminal.png"),
			usenet: icon("system/network/usenet.png"),
			userDirectory: icon("system/network/user-directory.png"),
		},
		quicktime: {
			backwardButton: icon("system/quicktime/backward-button.svg"),
			cc: icon("system/quicktime/cc.png"),
			controlPanel: icon("system/quicktime/control-panel.png"),
			extension: icon("system/quicktime/extension.png"),
			forwardButton: icon("system/quicktime/forward-button.svg"),
			fullscreenButton: icon("system/quicktime/fullscreen-button.svg"),
			movieStationary: icon("system/quicktime/movie-stationary.png"),
			movie: icon("system/quicktime/movie.png"),
			pauseButton: icon("system/quicktime/pause-button.svg"),
			playButton: icon("system/quicktime/play-button.svg"),
			player: icon("system/quicktime/player.png"),
			splash: icon("system/quicktime/splash.png"),
			thumbSlider: icon("system/quicktime/thumb-slider.svg"),
			volumeSlider: icon("system/quicktime/volume-slider.svg"),
		},
		richTextEditor: {
			menubar: {
				alignCenterActive: icon(
					"system/rich-text-editor/menubar/align-center-active.png",
				),
				alignCenterDepressed: icon(
					"system/rich-text-editor/menubar/align-center-depressed.png",
				),
				alignCenterOff: icon(
					"system/rich-text-editor/menubar/align-center-off.png",
				),
				alignLeftActive: icon(
					"system/rich-text-editor/menubar/align-left-active.png",
				),
				alignLeftDepressed: icon(
					"system/rich-text-editor/menubar/align-left-depressed.png",
				),
				alignLeftOff: icon(
					"system/rich-text-editor/menubar/align-left-off.png",
				),
				alignRightActive: icon(
					"system/rich-text-editor/menubar/align-right-active.png",
				),
				alignRightDepressed: icon(
					"system/rich-text-editor/menubar/align-right-depressed.png",
				),
				alignRightOff: icon(
					"system/rich-text-editor/menubar/align-right-off.png",
				),
				boldActive: icon("system/rich-text-editor/menubar/bold-active.png"),
				boldDepressed: icon(
					"system/rich-text-editor/menubar/bold-depressed.png",
				),
				boldOff: icon("system/rich-text-editor/menubar/bold-off.png"),
				capsActive: icon("system/rich-text-editor/menubar/caps-active.png"),
				capsDepressed: icon(
					"system/rich-text-editor/menubar/caps-depressed.png",
				),
				capsOff: icon("system/rich-text-editor/menubar/caps-off.png"),
				indentActive: icon("system/rich-text-editor/menubar/indent-active.png"),
				indentOff: icon("system/rich-text-editor/menubar/indent-off.png"),
				italicActive: icon("system/rich-text-editor/menubar/italic-active.png"),
				italicDepressed: icon(
					"system/rich-text-editor/menubar/italic-depressed.png",
				),
				italicOff: icon("system/rich-text-editor/menubar/italic-off.png"),
				orderedListActive: icon(
					"system/rich-text-editor/menubar/ordered-list-active.png",
				),
				orderedListDepressed: icon(
					"system/rich-text-editor/menubar/ordered-list-depressed.png",
				),
				orderedListOff: icon(
					"system/rich-text-editor/menubar/ordered-list-off.png",
				),
				outdentActive: icon(
					"system/rich-text-editor/menubar/outdent-active.png",
				),
				outdentOff: icon("system/rich-text-editor/menubar/outdent-off.png"),
				ruleActive: icon("system/rich-text-editor/menubar/rule-active.png"),
				ruleOff: icon("system/rich-text-editor/menubar/rule-off.png"),
				search: icon("system/rich-text-editor/menubar/search.png"),
				underlineActive: icon(
					"system/rich-text-editor/menubar/underline-active.png",
				),
				underlineDepressed: icon(
					"system/rich-text-editor/menubar/underline-depressed.png",
				),
				underlineOff: icon("system/rich-text-editor/menubar/underline-off.png"),
				unorderedListActive: icon(
					"system/rich-text-editor/menubar/unordered-list-active.png",
				),
				unorderedListDepressed: icon(
					"system/rich-text-editor/menubar/unordered-list-depressed.png",
				),
				unorderedListOff: icon(
					"system/rich-text-editor/menubar/unordered-list-off.png",
				),
			},
		},
		security: {
			id: icon("system/security/id.png"),
			key: icon("system/security/key.png"),
		},
		sherlock: {
			searchData: icon("system/sherlock/search-data.png"),
			searchFiles: icon("system/sherlock/search-files.png"),
			search: icon("system/sherlock/search.png"),
			sherlock: icon("system/sherlock/sherlock.png"),
		},
		tags: {
			colors: {
				aqua: icon("system/tags/colors/aqua.png"),
				black: icon("system/tags/colors/black.png"),
				blue: icon("system/tags/colors/blue.png"),
				fuchsia: icon("system/tags/colors/fuchsia.png"),
				gray: icon("system/tags/colors/gray.png"),
				green: icon("system/tags/colors/green.png"),
				lime: icon("system/tags/colors/lime.png"),
				maroon: icon("system/tags/colors/maroon.png"),
				navy: icon("system/tags/colors/navy.png"),
				olive: icon("system/tags/colors/olive.png"),
				purple: icon("system/tags/colors/purple.png"),
				red: icon("system/tags/colors/red.png"),
				silver: icon("system/tags/colors/silver.png"),
				teal: icon("system/tags/colors/teal.png"),
				white: icon("system/tags/colors/white.png"),
				yellow: icon("system/tags/colors/yellow.png"),
			},
		},
	},
	ui: {
		halftoneLarge: ui("halftone-large.png"),
		halftoneSmall: ui("halftone-small.png"),
		menuDropdownArrowUp: ui("menu-dropdown-arrow-up.svg"),
		menuDropdownArrows: ui("menu-dropdown-arrows.svg"),
		stripe: ui("stripe.svg"),
		tab: ui("tab.svg"),
		cursors: {
			add: ui("cursors/cursor-add.png"),
			eyedropper: ui("cursors/cursor-eyedropper.png"),
			handCounting: ui("cursors/cursor-hand-counting.png"),
			hand: ui("cursors/cursor-hand.png"),
			highlight: ui("cursors/cursor-highlight.png"),
			menu: ui("cursors/cursor-menu.png"),
			plus: ui("cursors/cursor-plus.png"),
			resizeD: ui("cursors/cursor-resize-d.png"),
			resizeL: ui("cursors/cursor-resize-l.png"),
			resizeLr: ui("cursors/cursor-resize-lr.png"),
			resizeR: ui("cursors/cursor-resize-r.png"),
			resizeU: ui("cursors/cursor-resize-u.png"),
			resizeUd: ui("cursors/cursor-resize-ud.png"),
			rom: ui("cursors/cursor-rom.png"),
			shortcut: ui("cursors/cursor-shortcut.png"),
			spin: ui("cursors/cursor-spin.png"),
			watch: ui("cursors/cursor-watch.png"),
			precision: ui("cursors/cursor-precision.png"),
		},
	},
};
